// modules/chariot/ui/src/features/integrations/config/cloudProviderConfigs.ts

/**
 * Cloud provider-specific configurations
 * Extracted from AWS.tsx, Azure.tsx, GoogleCloud.tsx to eliminate duplication
 */

import type {
  AWSIntegration,
  AzureIntegration,
  GCPIntegration,
} from '@/features/integrations/types';

// ============================================================================
// AWS Configuration
// ============================================================================

export const AWS_CONFIG: AWSIntegration = {
  key: 'aws',
  name: 'Amazon Web Services',
  category: 'cloud',
  description:
    'Connect your AWS account for automated security scanning and asset discovery',
  longDescription: `
Amazon Web Services (AWS) integration allows Chariot to discover and continuously monitor your AWS infrastructure for security vulnerabilities.

**Capabilities:**
- Automated asset discovery across all regions
- Security group analysis
- IAM policy review
- S3 bucket configuration audits
- EC2 instance scanning
- Lambda function security review

**Setup Requirements:**
- AWS account with administrative access
- Ability to create IAM roles
- External ID for secure cross-account access
  `,
  logo: '/logos/aws.svg',
  authMethod: 'aws-role',
  enabled: true,
  beta: false,
  tags: ['cloud', 'infrastructure', 'iam', 'security'],

  awsSpecific: {
    supportedRegions: [
      'us-east-1',
      'us-east-2',
      'us-west-1',
      'us-west-2',
      'eu-west-1',
      'eu-west-2',
      'eu-central-1',
      'ap-southeast-1',
      'ap-southeast-2',
      'ap-northeast-1',
    ],
    supportedServices: [
      'ec2',
      's3',
      'iam',
      'lambda',
      'rds',
      'dynamodb',
      'cloudfront',
      'route53',
      'elb',
      'vpc',
    ],
    iamPolicyTemplate: JSON.stringify(
      {
        Version: '2012-10-17',
        Statement: [
          {
            Effect: 'Allow',
            Action: [
              'ec2:Describe*',
              's3:List*',
              's3:GetBucketPolicy',
              'iam:List*',
              'iam:Get*',
              'lambda:List*',
              'rds:Describe*',
            ],
            Resource: '*',
          },
        ],
      },
      null,
      2
    ),
    externalIdRequired: true,
  },

  features: {
    supportsWebhooks: false,
    supportsScheduledScans: true,
    supportsCustomQueries: true,
    supportsHealthCheck: true,
    requiresWhitelisting: false,
    supportsBulkOperations: true,
    supportsRealTimeSync: false,
  },

  documentation: {
    setupGuide: 'https://docs.praetorian.com/chariot/integrations/aws',
    apiReference:
      'https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_create_for-user_externalid.html',
    troubleshooting: 'https://docs.praetorian.com/chariot/troubleshooting/aws',
  },

  wizardConfig: {
    steps: [
      {
        id: 'account-info',
        title: 'Account Information',
        description: 'Provide your AWS account details',
        fields: [
          {
            id: 'accountName',
            key: 'accountName',
            label: 'Account Name',
            type: 'text',
            placeholder: 'Production AWS Account',
            description: 'A friendly name to identify this AWS account',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Account name is required',
              },
              {
                type: 'min',
                value: 3,
                message: 'Account name must be at least 3 characters',
              },
            ],
          },
          {
            id: 'accountId',
            key: 'accountId',
            label: 'AWS Account ID',
            type: 'text',
            placeholder: '123456789012',
            description: 'Your 12-digit AWS account ID',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'AWS Account ID is required',
              },
              {
                type: 'pattern',
                value: /^\d{12}$/,
                message: 'AWS Account ID must be exactly 12 digits',
              },
            ],
            helpText:
              'Find your Account ID in the AWS Console under "My Account"',
            link: {
              text: 'How to find your Account ID',
              url: 'https://docs.aws.amazon.com/IAM/latest/UserGuide/console_account-alias.html',
            },
          },
        ],
      },
      {
        id: 'iam-role',
        title: 'IAM Role Setup',
        description: 'Create and configure the IAM role for Chariot',
        fields: [
          {
            id: 'externalId',
            key: 'externalId',
            label: 'External ID',
            type: 'text',
            placeholder: 'Generated automatically',
            description: 'Unique identifier for secure cross-account access',
            required: true,
            defaultValue: `chariot-${Date.now()}`,
            validation: [
              {
                type: 'required',
                message: 'External ID is required',
              },
            ],
            helpText: 'This External ID will be used in your IAM trust policy',
          },
          {
            id: 'roleArn',
            key: 'roleArn',
            label: 'IAM Role ARN',
            type: 'text',
            placeholder: 'arn:aws:iam::123456789012:role/ChariotRole',
            description: 'The ARN of the IAM role created for Chariot',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'IAM Role ARN is required',
              },
              {
                type: 'pattern',
                value: /^arn:aws:iam::\d{12}:role\/.+$/,
                message: 'Invalid IAM Role ARN format',
              },
            ],
            helpText: 'Copy the ARN from the IAM role summary page',
            link: {
              text: 'IAM Role Setup Guide',
              url: 'https://docs.praetorian.com/chariot/integrations/aws/iam-setup',
            },
          },
        ],
      },
      {
        id: 'configuration',
        title: 'Configuration',
        description: 'Configure scanning options',
        optional: true,
        fields: [
          {
            id: 'regions',
            key: 'regions',
            label: 'Regions to Scan',
            type: 'multiselect',
            description: 'Select AWS regions to include in scans',
            defaultValue: ['us-east-1'],
            options: [
              { label: 'US East (N. Virginia)', value: 'us-east-1' },
              { label: 'US East (Ohio)', value: 'us-east-2' },
              { label: 'US West (N. California)', value: 'us-west-1' },
              { label: 'US West (Oregon)', value: 'us-west-2' },
              { label: 'EU (Ireland)', value: 'eu-west-1' },
              { label: 'EU (London)', value: 'eu-west-2' },
              { label: 'EU (Frankfurt)', value: 'eu-central-1' },
              { label: 'Asia Pacific (Singapore)', value: 'ap-southeast-1' },
              { label: 'Asia Pacific (Sydney)', value: 'ap-southeast-2' },
              { label: 'Asia Pacific (Tokyo)', value: 'ap-northeast-1' },
            ],
          },
          {
            id: 'autoScan',
            key: 'autoScan',
            label: 'Enable Automatic Scanning',
            type: 'checkbox',
            description: 'Automatically scan for new assets daily',
            defaultValue: true,
          },
          {
            id: 'scanFrequency',
            key: 'scanFrequency',
            label: 'Scan Frequency',
            type: 'select',
            description: 'How often to run automated scans',
            defaultValue: 'daily',
            options: [
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' },
            ],
            dependsOn: {
              field: 'autoScan',
              value: true,
            },
          },
        ],
      },
      {
        id: 'verification',
        title: 'Verify Connection',
        description: 'Test the integration to ensure proper configuration',
        fields: [],
      },
    ],
    submitButtonText: 'Complete Setup',
    showProgressBar: true,
    allowSkipOptional: true,
  },
};

// ============================================================================
// Azure Configuration
// ============================================================================

export const AZURE_CONFIG: AzureIntegration = {
  key: 'azure',
  name: 'Microsoft Azure',
  category: 'cloud',
  description:
    'Connect your Azure subscription for automated security scanning and asset discovery',
  longDescription: `
Microsoft Azure integration enables Chariot to discover and continuously monitor your Azure infrastructure for security vulnerabilities.

**Capabilities:**
- Automated resource discovery across all regions
- Network security group analysis
- Azure AD security review
- Storage account configuration audits
- Virtual machine scanning
- Function app security review

**Setup Requirements:**
- Azure subscription with contributor access
- Ability to create service principals
- Azure AD permissions for identity review
  `,
  logo: '/logos/azure.svg',
  authMethod: 'azure-credentials',
  enabled: true,
  beta: false,
  tags: ['cloud', 'infrastructure', 'azure-ad', 'security'],

  azureSpecific: {
    requiredPermissions: [
      'Microsoft.Resources/subscriptions/read',
      'Microsoft.Compute/virtualMachines/read',
      'Microsoft.Network/networkSecurityGroups/read',
      'Microsoft.Storage/storageAccounts/read',
      'Microsoft.Web/sites/read',
    ],
    supportedSubscriptions: [],
    azureAdRequired: true,
  },

  features: {
    supportsWebhooks: false,
    supportsScheduledScans: true,
    supportsCustomQueries: true,
    supportsHealthCheck: true,
    requiresWhitelisting: false,
    supportsBulkOperations: true,
    supportsRealTimeSync: false,
  },

  documentation: {
    setupGuide: 'https://docs.praetorian.com/chariot/integrations/azure',
    apiReference:
      'https://docs.microsoft.com/en-us/azure/active-directory/develop/howto-create-service-principal-portal',
    troubleshooting:
      'https://docs.praetorian.com/chariot/troubleshooting/azure',
  },

  wizardConfig: {
    steps: [
      {
        id: 'subscription-info',
        title: 'Subscription Information',
        description: 'Provide your Azure subscription details',
        fields: [
          {
            id: 'subscriptionName',
            key: 'subscriptionName',
            label: 'Subscription Name',
            type: 'text',
            placeholder: 'Production Azure Subscription',
            description: 'A friendly name to identify this Azure subscription',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Subscription name is required',
              },
            ],
          },
          {
            id: 'subscriptionId',
            key: 'subscriptionId',
            label: 'Subscription ID',
            type: 'text',
            placeholder: '00000000-0000-0000-0000-000000000000',
            description: 'Your Azure subscription ID (GUID format)',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Subscription ID is required',
              },
              {
                type: 'pattern',
                value:
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                message: 'Invalid Subscription ID format',
              },
            ],
            helpText: 'Find your Subscription ID in the Azure Portal',
            link: {
              text: 'How to find your Subscription ID',
              url: 'https://docs.microsoft.com/en-us/azure/media-services/latest/setup-azure-subscription-how-to',
            },
          },
          {
            id: 'tenantId',
            key: 'tenantId',
            label: 'Tenant ID',
            type: 'text',
            placeholder: '00000000-0000-0000-0000-000000000000',
            description: 'Your Azure AD tenant ID',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Tenant ID is required',
              },
              {
                type: 'pattern',
                value:
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                message: 'Invalid Tenant ID format',
              },
            ],
          },
        ],
      },
      {
        id: 'service-principal',
        title: 'Service Principal',
        description: 'Create and configure a service principal for Chariot',
        fields: [
          {
            id: 'clientId',
            key: 'clientId',
            label: 'Application (Client) ID',
            type: 'text',
            placeholder: '00000000-0000-0000-0000-000000000000',
            description: 'The Application ID of your service principal',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Client ID is required',
              },
              {
                type: 'pattern',
                value:
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                message: 'Invalid Client ID format',
              },
            ],
            link: {
              text: 'Service Principal Setup Guide',
              url: 'https://docs.praetorian.com/chariot/integrations/azure/service-principal',
            },
          },
          {
            id: 'clientSecret',
            key: 'clientSecret',
            label: 'Client Secret',
            type: 'password',
            placeholder: 'Enter client secret',
            description: 'The client secret for authentication',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Client secret is required',
              },
            ],
            helpText: 'Create a new client secret in the Azure Portal',
          },
        ],
      },
      {
        id: 'configuration',
        title: 'Configuration',
        description: 'Configure scanning options',
        optional: true,
        fields: [
          {
            id: 'includeAzureAD',
            key: 'includeAzureAD',
            label: 'Include Azure AD Scanning',
            type: 'checkbox',
            description: 'Scan Azure AD for identity and access risks',
            defaultValue: true,
          },
          {
            id: 'autoScan',
            key: 'autoScan',
            label: 'Enable Automatic Scanning',
            type: 'checkbox',
            description: 'Automatically scan for new assets daily',
            defaultValue: true,
          },
          {
            id: 'scanFrequency',
            key: 'scanFrequency',
            label: 'Scan Frequency',
            type: 'select',
            description: 'How often to run automated scans',
            defaultValue: 'daily',
            options: [
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' },
            ],
            dependsOn: {
              field: 'autoScan',
              value: true,
            },
          },
        ],
      },
      {
        id: 'verification',
        title: 'Verify Connection',
        description: 'Test the integration to ensure proper configuration',
        fields: [],
      },
    ],
    submitButtonText: 'Complete Setup',
    showProgressBar: true,
    allowSkipOptional: true,
  },
};

// ============================================================================
// GCP Configuration
// ============================================================================

export const GCP_CONFIG: GCPIntegration = {
  key: 'gcp',
  name: 'Google Cloud Platform',
  category: 'cloud',
  description:
    'Connect your GCP project for automated security scanning and asset discovery',
  longDescription: `
Google Cloud Platform integration allows Chariot to discover and continuously monitor your GCP infrastructure for security vulnerabilities.

**Capabilities:**
- Automated resource discovery across all regions
- Firewall rule analysis
- IAM policy review
- Cloud Storage bucket audits
- Compute Engine instance scanning
- Cloud Function security review

**Setup Requirements:**
- GCP project with owner or editor access
- Ability to create service accounts
- Service account key file (JSON)
  `,
  logo: '/logos/gcp.svg',
  authMethod: 'gcp-service-account',
  enabled: true,
  beta: false,
  tags: ['cloud', 'infrastructure', 'iam', 'security'],

  gcpSpecific: {
    requiredScopes: [
      'https://www.googleapis.com/auth/cloud-platform.read-only',
      'https://www.googleapis.com/auth/compute.readonly',
    ],
    serviceAccountRoles: ['roles/viewer', 'roles/iam.securityReviewer'],
    projectIdRequired: true,
  },

  features: {
    supportsWebhooks: false,
    supportsScheduledScans: true,
    supportsCustomQueries: true,
    supportsHealthCheck: true,
    requiresWhitelisting: false,
    supportsBulkOperations: true,
    supportsRealTimeSync: false,
  },

  documentation: {
    setupGuide: 'https://docs.praetorian.com/chariot/integrations/gcp',
    apiReference:
      'https://cloud.google.com/iam/docs/creating-managing-service-accounts',
    troubleshooting: 'https://docs.praetorian.com/chariot/troubleshooting/gcp',
  },

  wizardConfig: {
    steps: [
      {
        id: 'project-info',
        title: 'Project Information',
        description: 'Provide your GCP project details',
        fields: [
          {
            id: 'projectName',
            key: 'projectName',
            label: 'Project Name',
            type: 'text',
            placeholder: 'Production GCP Project',
            description: 'A friendly name to identify this GCP project',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Project name is required',
              },
            ],
          },
          {
            id: 'projectId',
            key: 'projectId',
            label: 'Project ID',
            type: 'text',
            placeholder: 'my-project-123456',
            description: 'Your GCP project ID',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Project ID is required',
              },
              {
                type: 'pattern',
                value: /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/,
                message: 'Invalid Project ID format',
              },
            ],
            helpText: 'Find your Project ID in the GCP Console',
            link: {
              text: 'How to find your Project ID',
              url: 'https://cloud.google.com/resource-manager/docs/creating-managing-projects',
            },
          },
        ],
      },
      {
        id: 'service-account',
        title: 'Service Account',
        description: 'Create and configure a service account for Chariot',
        fields: [
          {
            id: 'serviceAccountEmail',
            key: 'serviceAccountEmail',
            label: 'Service Account Email',
            type: 'email',
            placeholder: 'chariot@my-project.iam.gserviceaccount.com',
            description: 'The email address of your service account',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Service account email is required',
              },
              {
                type: 'email',
                message: 'Invalid email format',
              },
            ],
            link: {
              text: 'Service Account Setup Guide',
              url: 'https://docs.praetorian.com/chariot/integrations/gcp/service-account',
            },
          },
          {
            id: 'serviceAccountKey',
            key: 'serviceAccountKey',
            label: 'Service Account Key (JSON)',
            type: 'file',
            description: 'Upload the JSON key file for your service account',
            required: true,
            validation: [
              {
                type: 'required',
                message: 'Service account key is required',
              },
              {
                type: 'custom',
                message: 'Invalid JSON key file',
                custom: (value: any) => {
                  try {
                    const parsed = JSON.parse(value);
                    return parsed.type === 'service_account';
                  } catch {
                    return false;
                  }
                },
              },
            ],
            helpText: 'Download the JSON key from the GCP Console',
          },
        ],
      },
      {
        id: 'configuration',
        title: 'Configuration',
        description: 'Configure scanning options',
        optional: true,
        fields: [
          {
            id: 'autoScan',
            key: 'autoScan',
            label: 'Enable Automatic Scanning',
            type: 'checkbox',
            description: 'Automatically scan for new assets daily',
            defaultValue: true,
          },
          {
            id: 'scanFrequency',
            key: 'scanFrequency',
            label: 'Scan Frequency',
            type: 'select',
            description: 'How often to run automated scans',
            defaultValue: 'daily',
            options: [
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' },
            ],
            dependsOn: {
              field: 'autoScan',
              value: true,
            },
          },
        ],
      },
      {
        id: 'verification',
        title: 'Verify Connection',
        description: 'Test the integration to ensure proper configuration',
        fields: [],
      },
    ],
    submitButtonText: 'Complete Setup',
    showProgressBar: true,
    allowSkipOptional: true,
  },
};

// ============================================================================
// Export Configuration Map
// ============================================================================

export const CLOUD_PROVIDER_CONFIGS = {
  aws: AWS_CONFIG,
  azure: AZURE_CONFIG,
  gcp: GCP_CONFIG,
} as const;

export type CloudProviderKey = keyof typeof CLOUD_PROVIDER_CONFIGS;
